import { useState, useEffect, useCallback, useRef } from 'react';
import fetchWithTenant from '../utils/fetchWithTenant';
import {
  ArrowLeft, RefreshCw, AlertCircle, Loader, Cpu, Clock, Cable, Router as RouterIcon,
} from 'lucide-react';
import DeviceHeader from '../components/DeviceHeader';
import ControlPill from '../components/ControlPill';
import DeviceCard from '../components/DeviceCard';
import DeviceListRow from '../components/DeviceListRow';
import '../styles/v2/components/device-detail.css';

const POLL_INTERVAL = 15000;

// ─── Never False Green: WAN state visuals ─────────────────────────────────────
// Colour is driven by the `state` field (not the raw LED) so STANDBY and
// DISABLED -- both gray on the router itself -- read differently here, which is
// exactly why Phase A split them. CONNECTED is the only "green/healthy" state;
// STANDBY is a calm blue "backup, ready"; DISABLED is neutral grey (admin turned
// it off, not a fault); DISCONNECTED/UNKNOWN are attention-needed.
export const WAN_STATE = {
  CONNECTED:    { color: '#10B981', bg: '#10B98118', border: '#10B98140', label: 'Connected',   healthy: true },
  STANDBY:      { color: '#3B82F6', bg: '#3B82F614', border: '#3B82F633', label: 'Standby' },
  CHECKING:     { color: '#F59E0B', bg: '#F59E0B14', border: '#F59E0B33', label: 'Checking' },
  DISABLED:     { color: '#6B7280', bg: '#6B728012', border: '#6B728033', label: 'Disabled', muted: true },
  DISCONNECTED: { color: '#EF4444', bg: '#EF444414', border: '#EF444440', label: 'Disconnected' },
  UNKNOWN:      { color: '#F59E0B', bg: '#F59E0B12', border: '#F59E0B33', label: 'Unknown' },
};

const WAN_TYPE = {
  ETHERNET: 'Ethernet',
  CELLULAR: 'Cellular',
  WIFI:     'Wi-Fi',
  WOVLAN:   'VLAN',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatUptime(sec) {
  if (!sec && sec !== 0) return '—';
  const d = Math.floor(sec / 86400), h = Math.floor((sec % 86400) / 3600), m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function wanStateCfg(state) {
  return WAN_STATE[state] || WAN_STATE.UNKNOWN;
}

// The primary WAN is the connectable link with the lowest positive priority.
// Priority 0 / null means the link is not in the failover order (typically
// disabled), so it never counts as primary.
export function findPrimaryWanId(links) {
  let best = null;
  for (const w of links) {
    if (typeof w.priority === 'number' && w.priority > 0) {
      if (!best || w.priority < best.priority) best = w;
    }
  }
  return best ? best.id : null;
}

// Sort: links in the failover order first (priority asc), disabled/unordered
// links (priority 0 / null) after, so the active primary sits at the top.
export function sortWanLinks(links) {
  return [...links].sort((a, b) => {
    const pa = (typeof a.priority === 'number' && a.priority > 0) ? a.priority : Infinity;
    const pb = (typeof b.priority === 'number' && b.priority > 0) ? b.priority : Infinity;
    if (pa !== pb) return pa - pb;
    return String(a.wanId).localeCompare(String(b.wanId), undefined, { numeric: true });
  });
}

// ─── WAN Link Card ────────────────────────────────────────────────────────────
export function WanCard({ link, isPrimary }) {
  const cfg = wanStateCfg(link.state);
  const typeLabel = WAN_TYPE[link.type] || link.type || '—';
  const showNet = link.state === 'CONNECTED' && (link.ip || link.gateway);

  return (
    <DeviceCard style={{
      borderColor: cfg.border,
      padding: 16,
      opacity: cfg.muted ? 0.72 : 1,
    }}>
      {/* Head: name + state */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {link.name || `WAN ${link.wanId}`}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 5, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ padding: '1px 8px', borderRadius: 4, background: '#1E293B', color: 'var(--text-secondary)', fontSize: 10, fontWeight: 600 }}>
              {typeLabel}
            </span>
            {isPrimary && (
              <span style={{ padding: '1px 8px', borderRadius: 4, background: '#10B98120', color: '#10B981', fontSize: 10, fontWeight: 700, letterSpacing: '0.04em' }}>
                PRIMARY
              </span>
            )}
            {typeof link.priority === 'number' && link.priority > 0 && !isPrimary && (
              <span style={{ padding: '1px 8px', borderRadius: 4, background: '#1E293B', color: 'var(--text-tertiary)', fontSize: 10, fontWeight: 600 }}>
                Priority {link.priority}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, padding: '4px 10px', borderRadius: 20, background: cfg.bg, border: `1px solid ${cfg.border}` }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: cfg.color, boxShadow: cfg.healthy ? `0 0 6px ${cfg.color}` : 'none' }} />
          <span style={{ fontSize: 11, fontWeight: 600, color: cfg.color, whiteSpace: 'nowrap' }}>{cfg.label}</span>
        </div>
      </div>

      {/* Network details (only meaningful while connected) */}
      {showNet ? (
        <DeviceListRow style={{ padding: 10 }}>
          {[
            { k: 'IP Address', v: link.ip },
            { k: 'Gateway', v: link.gateway },
            { k: 'Link Uptime', v: link.uptimeSeconds ? formatUptime(link.uptimeSeconds) : null },
          ].filter(r => r.v).map((r, i, arr) => (
            <div key={r.k} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: i < arr.length - 1 ? '1px solid #1A2235' : 'none' }}>
              <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{r.k}</span>
              <span style={{ fontSize: 11, color: 'var(--text-primary)', fontFamily: 'monospace' }}>{r.v}</span>
            </div>
          ))}
        </DeviceListRow>
      ) : (
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', padding: '2px 0' }}>
          {link.state === 'DISABLED' ? 'Administratively disabled' : link.message || 'No active connection'}
        </div>
      )}
    </DeviceCard>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
// `sw` is the MdSwitchDevice row already loaded by MdSwitchDetail (hostname,
// managementIp, isOnline, category). We fetch the correlated MdRouterDevice
// (real firmware / WAN links / CPU / uptime) from the dedicated endpoint.
export default function MdRouterDetail({ sw, navigate }) {
  const [router, setRouter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [reclassifying, setReclassifying] = useState(false);
  const [reclassifyError, setReclassifyError] = useState(null);
  const timerRef = useRef(null);

  const fetchRouter = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetchWithTenant(`/api/md/switches/${sw.id}/router`, {});
      if (!res.ok) throw new Error(res.status === 404 ? 'No router telemetry yet' : `HTTP ${res.status}`);
      const data = await res.json();
      setRouter(data.router || data);
      setError(null);
    } catch (e) { setError(e.message); }
    finally { if (!silent) setLoading(false); }
  }, [sw.id]);

  useEffect(() => {
    fetchRouter();
    timerRef.current = setInterval(() => fetchRouter(true), POLL_INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [fetchRouter]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchRouter(true);
    setRefreshing(false);
  };

  // Phase 1 (868m1ytt0): same reclassify control and endpoint as
  // MdSwitchDetail.jsx. sw is a prop owned by the parent (MdSwitchDetail),
  // so a category change away from ROUTER needs the parent to re-fetch and
  // pick the right branch -- reload rather than plumb a callback prop
  // through, since this file is the only one this change touches.
  const handleReclassify = async (e) => {
    const category = e.target.value;
    if (category === (sw.category || 'ROUTER')) return;
    setReclassifying(true);
    setReclassifyError(null);
    try {
      const res = await fetchWithTenant(`/api/md/switches/${sw.id}/category`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      window.location.reload();
    } catch (err) {
      setReclassifyError(`Reclassify failed: ${err.message}`);
      setReclassifying(false);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, gap: 10, color: 'var(--text-tertiary)', fontFamily: 'system-ui' }}>
      <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} /> Loading router…
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (error || !router) return (
    <div style={{ padding: 24, fontFamily: 'system-ui' }}>
      <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, padding: 0, marginBottom: 16 }}>
        <ArrowLeft size={14} /> Back
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 16, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, color: '#ef4444', fontSize: 13 }}>
        <AlertCircle size={15} /> {error || 'Router telemetry not found'}
      </div>
    </div>
  );

  const links = sortWanLinks(router.wanLinks || []);
  const primaryWanId = findPrimaryWanId(router.wanLinks || []);
  const connectedCount = links.filter(l => l.state === 'CONNECTED').length;
  const mac = router.macAddress || sw.macAddress || '—';

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", background: '#0C111B', color: 'var(--text-primary)', minHeight: '100vh', padding: '20px 24px' }}>

      {/* Header */}
      <DeviceHeader
        icon={RouterIcon}
        online={sw.isOnline}
        title={sw.hostname || router.hostname || sw.managementIp}
        identityParts={[router.model || sw.model, router.serial || '—', router.ipAddress || sw.managementIp]}
        backLabel="Network"
        onBack={() => navigate(-1)}
      >
          <ControlPill variant="interactive">
            <select
              className="dd-pill-select"
              value={sw.category || 'ROUTER'}
              onChange={handleReclassify}
              disabled={reclassifying}
              title="Reclassify device category"
            >
              <option value="SWITCH">Switch</option>
              <option value="ROUTER">Router</option>
              <option value="ACCESS_POINT">Access Point</option>
              <option value="FIREWALL">Firewall</option>
              <option value="UNKNOWN">Unclassified</option>
            </select>
          </ControlPill>
          <ControlPill variant="passive" icon={Cable} label="FW" value={router.firmwareVersion || '—'} />
          <ControlPill variant="passive" icon={Clock} value={formatUptime(router.uptimeSeconds)} />
          <ControlPill variant="passive" icon={Cpu} value={`${router.cpuLoadPercent ?? '—'}%`} />
          <ControlPill variant="passive" label="MAC" value={mac} />
          <button onClick={handleRefresh} disabled={refreshing} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: '1px solid #1E293B', background: '#131B2E', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500, cursor: refreshing ? 'default' : 'pointer' }}>
            <RefreshCw size={13} style={refreshing ? { animation: 'spin 1s linear infinite' } : undefined} /> Refresh
          </button>
      </DeviceHeader>

      {reclassifyError && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', marginBottom: 16, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, color: '#ef4444', fontSize: 12 }}>
          <AlertCircle size={14} /> {reclassifyError}
        </div>
      )}

      {/* WAN section */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 14, fontWeight: 600 }}>WAN Links</span>
        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
          {connectedCount} connected · {links.length} total
        </span>
      </div>

      {links.length > 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {links.map(link => (
            <WanCard key={link.id} link={link} isPrimary={link.id === primaryWanId} />
          ))}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '40px 0', color: 'var(--text-tertiary)' }}>
          <Cable size={32} strokeWidth={1} />
          <span style={{ fontSize: 13 }}>No WAN links reported yet — waiting for first successful poll</span>
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        ::-webkit-scrollbar { height: 5px; width: 5px; }
        ::-webkit-scrollbar-track { background: #0C111B; }
        ::-webkit-scrollbar-thumb { background: #1E293B; border-radius: 3px; }
      `}</style>
    </div>
  );
}
